import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { Contract, TableColumn } from '../../types';
import { ContractForm } from '../../components/Form';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { API_CONFIG } from '../../config/api.config';
import {
  useContracts,
  useCreateContract,
  useUpdateContract,
  useDeleteContract,
} from '../../hooks/useContracts';
import { useClients } from '../../hooks/useClients';
import { useProviders } from '../../hooks/useProviders';
import { useBranches } from '../../hooks/useBranches';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

const Contracts: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | undefined>(undefined);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: contracts, isLoading, error, refetch } = useContracts(queryOptions);
  const { data: clients = [] } = useClients(queryOptions);
  const { data: providers = [] } = useProviders(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);
  const { data: currencies = [] } = useCurrencies();

  const createMutation = useCreateContract(() => {
    setShowForm(false);
    setEditingContract(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateContract(() => {
    setShowForm(false);
    setEditingContract(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteContract(() => {
    setTimeout(() => refetch(), 100);
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');

  const getParty = useCallback((contract: Contract) => {
    const client = clients.find((c: any) => c.documentId === contract.clientId || String(c.id) === String(contract.clientId));
    if (client) return { name: client.name, type: 'Client' };
    const provider = providers.find((p: any) => p.documentId === (contract as any).providerId || String(p.id) === String((contract as any).providerId));
    if (provider) return { name: provider.name, type: 'Supplier' };
    return { name: '-', type: '-' };
  }, [clients, providers]);

  const getBranchName = (branchId?: string) => {
    if (!branchId) return '-';
    const branch = branches.find((b: any) => b.documentId === branchId || String(b.id) === String(branchId));
    return branch ? branch.name : '-';
  };

  const getDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : '-';
  };

  const filteredContracts = useMemo(() => {
    if (!contracts) return [];
    return contracts.filter(contract => {
      const party = getParty(contract);
      const matchesSearch = !searchTerm || (
        party.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
      const matchesType = typeFilter === 'all' || contract.type === typeFilter;
      const matchesCurrency = currencyFilter === 'all' ||
        resolveCurrencyCode((contract as any).currency, currencies) === currencyFilter;
      const startDate = new Date(contract.startDate);
      const matchesDateFrom = !dateFromFilter || startDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || startDate <= new Date(dateToFilter);
      const matchesAmountFrom = !amountFromFilter || contract.value >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || contract.value <= parseFloat(amountToFilter);
      return matchesSearch && matchesStatus && matchesType && matchesCurrency &&
        matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [contracts, currencies, searchTerm, statusFilter, typeFilter, currencyFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, getParty]);

  const handleExport = () => {
    const exportData = filteredContracts.map(contract => ({
      'Party': `${getParty(contract).name} (${getParty(contract).type})`,
      'Type': contract.type,
      'Title': contract.title,
      'Value': contract.value,
      'Currency': (contract as any).currency ? resolveCurrencyCode((contract as any).currency, currencies) : '-',
      'Status': contract.status,
      'Start Date': new Date(contract.startDate).toLocaleDateString(),
      'End Date': new Date(contract.endDate).toLocaleDateString(),
      'Duration (days)': getDuration(contract.startDate, contract.endDate),
      'Vessel/Headquarters': getBranchName(contract.branchId),
      'Description': contract.description
    }));
    exportToExcel(exportData, `contracts_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredContracts.map(contract => ({
      'Party': `${getParty(contract).name} (${getParty(contract).type})`,
      'Type': contract.type,
      'Title': contract.title,
      'Value': contract.value,
      'Currency': (contract as any).currency ? resolveCurrencyCode((contract as any).currency, currencies) : '-',
      'Status': contract.status,
      'Start Date': new Date(contract.startDate).toLocaleDateString(),
      'End Date': new Date(contract.endDate).toLocaleDateString(),
      'Duration (days)': getDuration(contract.startDate, contract.endDate),
      'Vessel/Headquarters': getBranchName(contract.branchId),
      'Description': contract.description
    }));
    printAsExcel(exportData, 'Contracts');
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }] },
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter, options: [{ value: 'all', label: 'All Types' }, { value: 'charter', label: 'Charter' }, { value: 'service', label: 'Service' }, { value: 'supply', label: 'Supply' }, { value: 'maintenance', label: 'Maintenance' }] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [
      { value: 'all', label: 'All Currencies' },
      ...(currencies as any[])
        .filter((c: any) => c.isActive !== false && c.code)
        .map((c: any) => ({ value: c.code, label: c.code }))
    ] },
    { key: 'dateFrom', label: 'Start Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Start Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Value', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Value', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCurrencyFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setAmountFromFilter('');
    setAmountToFilter('');
  };

  const columns: TableColumn[] = [
    { key: 'party', label: 'Party', sortable: true, render: (_: any, item: Contract) => {
      const party = getParty(item);
      return `${party.name} (${party.type})`;
    } },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'value', label: 'Value', sortable: true, render: (value: number | null, item: Contract) => {
      const code = (item as any).currency ? resolveCurrencyCode((item as any).currency, currencies) : '';
      const symbol = code === 'USD' ? '$' : code === 'EUR' ? '€' : code;
      return `${symbol ? `${symbol} ` : ''}${(value ?? 0).toLocaleString()}`;
    } },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => {
      let color = 'bg-gray-100 text-gray-800';
      if (value === 'active') color = 'bg-green-100 text-green-800';
      else if (value === 'completed') color = 'bg-blue-100 text-blue-800';
      else if (value === 'cancelled') color = 'bg-red-100 text-red-800';
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{value}</span>;
    } },
    { key: 'startDate', label: 'Start Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'endDate', label: 'End Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'duration', label: 'Duration (days)', sortable: false, render: (_: any, item: Contract) => getDuration(item.startDate, item.endDate) },
    { key: 'branchId', label: 'Branch', sortable: false, render: (_: any, item: Contract) => getBranchName(item.branchId!) },
    { key: 'description', label: 'Description', sortable: false },
    { key: 'documents', label: 'Documents', sortable: false, render: (value: any) => {
      const docs = Array.isArray(value) ? value.filter((d: any) => d && typeof d === 'object' && d.url) : [];
      if (docs.length === 0) return <span className="text-gray-400">-</span>;
      return (
        <div className="flex flex-col gap-1">
          {docs.map((doc: any, i: number) => (
            <a key={i} href={doc.url.startsWith('http') ? doc.url : `${API_CONFIG.BASE_URL}${doc.url}`} target="_blank" rel="noreferrer" className="text-ocean-600 hover:underline text-xs truncate max-w-[160px]">
              {doc.name || `File ${i + 1}`}
            </a>
          ))}
        </div>
      );
    } },
    { key: 'notes', label: 'Notes', sortable: false },
  ];

  const handleEdit = (contract: Contract) => {
    setEditingContract(contract);
    setShowForm(true);
  };

  const handleDelete = async (contract: Contract) => {
    if (!window.confirm(`Are you sure you want to delete contract ${contract.id}?`)) return;
    if (!contract?.id) return;
    try {
      await deleteMutation.execute(contract);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingContract(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Contract>) => {
    try {
      if (editingContract?.id) {
        const updateData = { ...data, documentId: editingContract.documentId };
        await updateMutation.execute({ id: editingContract.id, data: updateData });
      } else {
        await createMutation.execute(data);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingContract(undefined);
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading contracts. Please try again." onRetry={refetch} entityName="contracts" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Contracts" description="Manage contracts for clients and suppliers, including duration, branch, documents, and notes" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search contracts..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={contracts?.length ?? 0}
        filteredCount={filteredContracts.length}
        entityName="contracts"
        onAdd={handleAdd}
        addLabel="Add Contract"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredContracts}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title=""
        searchPlaceholder=""
      />
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <ContractForm
              contract={editingContract}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
